import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, StatCard, Table, useTable } from "@repo/o37-ui";
import { ArrowLeft, Edit, Trash2, Plus, Building, Users, Calendar, CreditCard, Clock } from "lucide-react";
import {
  HoldingsSubscription,
  HoldingsSubscriptionSeat,
  HoldingsSubscriptionHistory,
  HOLDINGS_SUBSCRIPTION_STATUSES,
  HOLDINGS_BILLING_CYCLES,
  formatCents,
  getMonthlyEquivalent,
  getAnnualEquivalent
} from '../../types';
import SubscriptionForm from '../../components/Holdings/SubscriptionForm';
import SeatAssignmentForm from '../../components/Holdings/SeatAssignmentForm';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function SubscriptionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<HoldingsSubscription | null>(null);
  const [seats, setSeats] = useState<HoldingsSubscriptionSeat[]>([]);
  const [history, setHistory] = useState<HoldingsSubscriptionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSeatForm, setShowSeatForm] = useState(false);
  const [editingSeat, setEditingSeat] = useState<HoldingsSubscriptionSeat | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    if (id) {
      loadSubscription();
    }
  }, [id]);

  const loadSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/subscriptions/${id}`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription({
          id: data.id,
          vendorName: data.vendor_name,
          description: data.description,
          costCents: data.cost_cents,
          billingCycle: data.billing_cycle,
          renewalDate: data.renewal_date,
          startDate: data.start_date,
          totalSeats: data.total_seats || 0,
          category: data.category,
          status: data.status,
          autoRenew: data.auto_renew,
          contractUrl: data.contract_url,
          notes: data.notes,
          createdBy: data.created_by,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        });

        // Load seats
        const seatsData = data.holdings_subscription_seats || [];
        setSeats(seatsData.map((s: any) => ({
          id: s.id,
          subscriptionId: data.id,
          companyId: s.company?.id || null,
          personId: s.person?.id || null,
          seatsAssigned: s.seats_assigned,
          notes: s.notes,
          assignedAt: s.assigned_at,
          company: s.company ? {
            id: s.company.id,
            name: s.company.name
          } : undefined,
          person: s.person ? {
            id: s.person.id,
            name: s.person.name
          } : undefined
        })));

        // Load history
        const historyResponse = await fetch(`${API_URL}/holdings/subscriptions/${id}/history`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        });
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setHistory(historyData.map((h: any) => ({
            id: h.id,
            subscriptionId: h.subscription_id,
            costCents: h.cost_cents,
            billingCycle: h.billing_cycle,
            totalSeats: h.total_seats,
            effectiveDate: h.effective_date,
            changeReason: h.change_reason,
            createdAt: h.created_at
          })));
        }
      } else if (response.status === 404) {
        navigate('/holdings/costs');
      }
    } catch (e) {
      console.error('Failed to load subscription:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this subscription? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/holdings/subscriptions/${id}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        navigate('/holdings/costs');
      }
    } catch (e) {
      console.error('Failed to delete subscription:', e);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSeat = async (seatId: string) => {
    if (!confirm('Remove this seat assignment?')) return;

    try {
      const response = await fetch(`${API_URL}/holdings/subscriptions/${id}/seats/${seatId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        loadSubscription();
      }
    } catch (e) {
      console.error('Failed to delete seat:', e);
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    const found = HOLDINGS_SUBSCRIPTION_STATUSES.find(s => s.status === status);
    return found?.color || 'bg-neutral-100 text-neutral-500';
  };

  const getBillingLabel = (cycle: string) => {
    const found = HOLDINGS_BILLING_CYCLES.find(c => c.cycle === cycle);
    return found?.label || cycle;
  };

  const assignedSeats = seats.reduce((sum, s) => sum + s.seatsAssigned, 0);
  const unassignedSeats = subscription ? subscription.totalSeats - assignedSeats : 0;

  const seatColumns = useMemo(() => [
    {
      id: "assignee",
      header: "Assigned To",
      cell: ({ row }: { row: { original: HoldingsSubscriptionSeat } }) => {
        const seat = row.original;
        // Both company and person assigned
        if (seat.company && seat.person) {
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-neutral-400" />
                <span className="font-medium">{seat.person.name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-500">
                <Building className="w-3.5 h-3.5 text-neutral-300" />
                <span>at {seat.company.name}</span>
              </div>
            </div>
          );
        }
        // Company only
        if (seat.company) {
          return (
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-neutral-400" />
              <span className="font-medium">{seat.company.name}</span>
              <span className="text-xs text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded">Company Pool</span>
            </div>
          );
        }
        // Person only
        if (seat.person) {
          return (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-neutral-400" />
              <span className="font-medium">{seat.person.name}</span>
            </div>
          );
        }
        return <span className="text-neutral-400">Unknown</span>;
      }
    },
    {
      id: "seats",
      header: "Seats",
      cell: ({ row }: { row: { original: HoldingsSubscriptionSeat } }) => (
        <span className="text-neutral-900">{row.original.seatsAssigned}</span>
      )
    },
    {
      id: "notes",
      header: "Notes",
      cell: ({ row }: { row: { original: HoldingsSubscriptionSeat } }) => (
        <span className="text-neutral-500 text-sm truncate max-w-xs">
          {row.original.notes || '-'}
        </span>
      )
    },
    {
      id: "assigned",
      header: "Assigned",
      cell: ({ row }: { row: { original: HoldingsSubscriptionSeat } }) => (
        <span className="text-neutral-500 text-sm">
          {formatDate(row.original.assignedAt)}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      size: 120,
      meta: {
        headerClassName: "text-right",
        cellClassName: "justify-end"
      },
      cell: ({ row }: { row: { original: HoldingsSubscriptionSeat } }) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            className="h-8 text-xs px-3"
            onClick={() => {
              setEditingSeat(row.original);
              setShowSeatForm(true);
            }}
            text="Edit"
          />
          <Button
            variant="outline"
            className="h-8 text-xs px-3 text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => handleDeleteSeat(row.original.id)}
            text="Remove"
          />
        </div>
      )
    }
  ], []);

  const { table: seatsTable, ...seatsTableProps } = useTable({
    data: seats,
    columns: seatColumns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: seats.length,
    resourceName: (plural) => `assignment${plural ? "s" : ""}`,
  });

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Loading...</h1>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Subscription not found</h1>
        <Button onClick={() => navigate('/holdings/costs')} text="Back to Costs" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/holdings/costs')}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Costs</span>
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold">{subscription.vendorName}</h1>
              <span className={`text-sm px-3 py-1 rounded-full font-medium ${getStatusColor(subscription.status)}`}>
                {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </span>
            </div>
            {subscription.description && (
              <p className="text-neutral-500">{subscription.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowForm(true)}
              icon={<Edit className="w-4 h-4" />}
              text="Edit"
            />
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-600 border-red-200 hover:bg-red-50"
              icon={<Trash2 className="w-4 h-4" />}
              text={deleting ? 'Deleting...' : 'Delete'}
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Cost"
          value={formatCents(subscription.costCents)}
          hint={`Per ${getBillingLabel(subscription.billingCycle).toLowerCase()}`}
        />
        <StatCard
          label="Monthly Equivalent"
          value={formatCents(getMonthlyEquivalent(subscription.costCents, subscription.billingCycle))}
          hint="Normalized cost"
        />
        <StatCard
          label="Annual Cost"
          value={formatCents(getAnnualEquivalent(subscription.costCents, subscription.billingCycle))}
          hint="Yearly projection"
        />
        <StatCard
          label="Seats"
          value={`${assignedSeats}/${subscription.totalSeats}`}
          hint={unassignedSeats > 0 ? `${unassignedSeats} unassigned` : 'All assigned'}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Details */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Subscription Details</h2>

            <div className="grid grid-cols-2 gap-6">
              {subscription.category && (
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-neutral-500">Category</div>
                    <div className="text-neutral-900">{subscription.category}</div>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-neutral-400 mt-0.5" />
                <div>
                  <div className="text-sm text-neutral-500">Billing Cycle</div>
                  <div className="text-neutral-900">{getBillingLabel(subscription.billingCycle)}</div>
                </div>
              </div>

              {subscription.renewalDate && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-neutral-500">Next Renewal</div>
                    <div className="text-neutral-900">{formatDate(subscription.renewalDate)}</div>
                  </div>
                </div>
              )}

              {subscription.startDate && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-neutral-500">Start Date</div>
                    <div className="text-neutral-900">{formatDate(subscription.startDate)}</div>
                  </div>
                </div>
              )}
            </div>

            {subscription.contractUrl && (
              <div className="mt-6 pt-6 border-t border-neutral-100">
                <a
                  href={subscription.contractUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm"
                >
                  View Contract
                </a>
              </div>
            )}

            {subscription.notes && (
              <div className="mt-6 pt-6 border-t border-neutral-100">
                <div className="text-sm text-neutral-500 mb-2">Notes</div>
                <p className="text-neutral-600 whitespace-pre-wrap">{subscription.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost History */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Cost History</h2>
            {history.length > 0 ? (
              <div className="space-y-3">
                {history.slice(0, 5).map((h) => (
                  <div
                    key={h.id}
                    className="p-3 bg-neutral-50 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-neutral-900">
                        {formatCents(h.costCents)}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {formatDate(h.effectiveDate)}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-500">
                      {getBillingLabel(h.billingCycle)} - {h.totalSeats || 0} seats
                    </div>
                    {h.changeReason && (
                      <div className="text-xs text-neutral-400 mt-1">{h.changeReason}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Clock className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>No history yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Seat Assignments */}
      {subscription.totalSeats > 0 && (
        <div className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Seat Assignments</h2>
                {unassignedSeats > 0 && (
                  <Button
                    onClick={() => {
                      setEditingSeat(null);
                      setShowSeatForm(true);
                    }}
                    icon={<Plus className="w-4 h-4" />}
                    text="Assign Seats"
                  />
                )}
              </div>

              {seats.length > 0 ? (
                <Table {...seatsTableProps} table={seatsTable} />
              ) : (
                <div className="text-center py-8 text-neutral-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                  <p className="mb-4">No seats assigned yet</p>
                  <Button
                    onClick={() => {
                      setEditingSeat(null);
                      setShowSeatForm(true);
                    }}
                    text="Assign Seats"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Subscription Form Modal */}
      {showForm && (
        <SubscriptionForm
          subscription={subscription}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            loadSubscription();
          }}
        />
      )}

      {/* Seat Assignment Form Modal */}
      {showSeatForm && (
        <SeatAssignmentForm
          subscriptionId={subscription.id}
          maxSeats={unassignedSeats + (editingSeat?.seatsAssigned || 0)}
          seat={editingSeat}
          onClose={() => {
            setShowSeatForm(false);
            setEditingSeat(null);
          }}
          onSaved={() => {
            setShowSeatForm(false);
            setEditingSeat(null);
            loadSubscription();
          }}
        />
      )}
    </div>
  );
}
